import { call, put, takeLatest } from 'redux-saga/effects';
import {
  CASE_TRIGGER_CRAWL_BEGIN,
  CASE_TRIGGER_CRAWL_SUCCESS,
  CASE_TRIGGER_CRAWL_FAILURE,
  CASE_TRIGGER_CRAWL_DISMISS_FEEDBACK,
} from './constants';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { withCurrentCaseId } from '../../../common/selectors';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';

export const triggerCrawl = createPromiseAction(CASE_TRIGGER_CRAWL_BEGIN);

export function dismissTriggerCrawlFeedback() {
  return {
    type: CASE_TRIGGER_CRAWL_DISMISS_FEEDBACK,
  };
}

export function* doTriggerCrawl(action) {
  const {
    payload: { caseId },
  } = action;
  const res = yield call(api.post, `/caseintel/crawl`, {
    caseId,
    triggeredBy: 'manual',
    forceReprocess: true,
    maxDocuments: 2,
  });

  if (res && res.error) {
    yield put({
      type: CASE_TRIGGER_CRAWL_FAILURE,
      feedback: {
        message: 'generic.triggerCrawlFailure',
        error: res.error,
      },
    });
    return yield call(rejectPromiseAction, action, res.error);
  }

  yield put({
    type: CASE_TRIGGER_CRAWL_SUCCESS,
    data: res,
  });
  yield call(resolvePromiseAction, action, res);
}

export function* watchTriggerCrawl() {
  yield takeLatest(triggerCrawl, withCurrentCaseId(doTriggerCrawl));
}

export function useTriggerCrawl() {
  const dispatch = useDispatch();

  const { triggerCrawlPending, triggerCrawlFeedback } = useSelector(
    (state) => ({
      triggerCrawlPending: state.case.triggerCrawlPending,
      triggerCrawlFeedback: state.case.triggerCrawlFeedback,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(triggerCrawl(...args));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissTriggerCrawlFeedback());
  }, [dispatch]);

  return {
    triggerCrawl: boundAction,
    triggerCrawlPending,
    triggerCrawlFeedback,
    dismissTriggerCrawlFeedback: boundDismissFeedback,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_TRIGGER_CRAWL_BEGIN + '.TRIGGER':
      return {
        ...state,
        triggerCrawlPending: true,
        triggerCrawlFeedback: null,
      };

    case CASE_TRIGGER_CRAWL_SUCCESS:
      return {
        ...state,
        triggerCrawlPending: false,
        triggerCrawlFeedback: action.feedback,
      };

    case CASE_TRIGGER_CRAWL_FAILURE:
      return {
        ...state,
        triggerCrawlPending: false,
        triggerCrawlFeedback: action.feedback,
      };

    case CASE_TRIGGER_CRAWL_DISMISS_FEEDBACK:
      return {
        ...state,
        triggerCrawlFeedback: null,
      };

    default:
      return state;
  }
}
